import React, { type ComponentProps, forwardRef } from 'react';
import { Controller } from 'react-hook-form';
import type { TextInput } from 'react-native';

import { MaskedTextField as GarnishMaskedTextField } from '../MaskedTextField';
import type { ControlledProps, FormProps } from './types';

// ─────────────────────────────────────────────────────────────────────────────

const MaskedTextFieldRaw = forwardRef<TextInput, Props['Raw']>((props, ref) => {
  const {
    name,
    control,
    rules,
    notice,
    onChangeText,
    transformValue,
    ...restProps
  } = props;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={(renderProps) => {
        const { field, fieldState } = renderProps;

        const { onChange, onBlur, value } = field;
        const { invalid, error } = fieldState;

        const formattedValue = transformValue ? transformValue(value) : value;

        const handleTextChange = (currentValue: string, rawValue: string) => {
          onChange(rawValue);
          onChangeText?.(currentValue, rawValue);
        };

        return (
          <GarnishMaskedTextField.Raw
            ref={ref}
            value={formattedValue}
            notice={notice ?? error?.message}
            noticePalette={invalid ? 'caution' : undefined}
            onBlur={onBlur}
            onChangeText={handleTextChange}
            autoCorrect={false}
            autoCapitalize="none"
            {...restProps}
          />
        );
      }}
    />
  );
});

const MaskedTextFieldCurrency = forwardRef<TextInput, Props['Currency']>(
  (props, ref) => {
    const {
      name,
      control,
      rules,
      notice,
      onChangeText,
      transformValue,
      ...restProps
    } = props;

    return (
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={(renderProps) => {
          const { field, fieldState } = renderProps;

          const { onChange, onBlur, value } = field;
          const { invalid, error } = fieldState;

          const formattedValue = transformValue ? transformValue(value) : value;

          const handleTextChange = (currentValue: string, rawValue: string) => {
            onChange(rawValue);
            onChangeText?.(currentValue, rawValue);
          };

          return (
            <GarnishMaskedTextField.Currency
              ref={ref}
              value={formattedValue}
              notice={notice ?? error?.message}
              noticePalette={invalid ? 'caution' : undefined}
              onBlur={onBlur}
              onChangeText={handleTextChange}
              autoCorrect={false}
              autoCapitalize="none"
              {...restProps}
            />
          );
        }}
      />
    );
  },
);

// ─────────────────────────────────────────────────────────────────────────────

export const MaskedTextField = {
  Raw: MaskedTextFieldRaw,
  Currency: MaskedTextFieldCurrency,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  Raw: FormProps &
    Omit<ComponentProps<typeof GarnishMaskedTextField.Currency>, OmittedProps> &
    ExtraProps;
  Currency: FormProps &
    Omit<ComponentProps<typeof GarnishMaskedTextField.Currency>, OmittedProps> &
    ExtraProps;
};

type OmittedProps = ControlledProps | 'noticePalette';

type ExtraProps = {
  transformValue?: (value: number | string | undefined) => string;
};
